import React, { useEffect, useState, forwardRef, useCallback, useRef } from "react";
import { textareaStyle, captionStyle } from "styles/global-style";

const Textarea = forwardRef((props, ref) => {
  const { customStyle, style, white, caption, left, onChange, onChangeDelay, value, minHeight, ...rest } = props;
  
  const [textValue, setTextValue] = useState(value ? value : "");
  const [textareaHeight, setTextareaHeight] = useState(100);
  const submitTimer = useRef();
  const hasChanged = useRef(false);
  const textareaRef = useRef();

  //----------------------------------------------------

  const submit = useCallback(
    (event, notimer) => {
      if (submitTimer.current) clearTimeout(submitTimer.current);
      if (onChange) {
        onChange(event);
        hasChanged.current = false;
      } else if (onChangeDelay) {
        if (!notimer) {
          submitTimer.current = setTimeout(() => {
            onChangeDelay(event);
            hasChanged.current = false;
          }, 3000);
        } else {
          // On blur or leaving page
          onChangeDelay(event);
          hasChanged.current = false;
        }
      }
    },
    [onChange, onChangeDelay]
  );

  //----------------------------------------------------

  const adjustHeight = useCallback(() => {
    if (textareaRef.current) {
      // Reset height to auto to get the scroll height
      textareaRef.current.style.height = 'auto';
      
      // Calculate the new height based on scroll height, with minimum (default 100px or custom minHeight)
      const defaultMinHeight = minHeight !== undefined ? minHeight : 100;
      let newHeight = Math.max(defaultMinHeight, textareaRef.current.scrollHeight);
      
      // Respect maxHeight if set in customStyle
      const computedStyle = window.getComputedStyle(textareaRef.current);
      const maxHeight = computedStyle.maxHeight;
      if (maxHeight && maxHeight !== 'none') {
        const maxHeightValue = parseFloat(maxHeight);
        if (!isNaN(maxHeightValue)) {
          newHeight = Math.min(newHeight, maxHeightValue);
        }
      }
      
      setTextareaHeight(newHeight);
      
      // Set the height
      textareaRef.current.style.height = `${newHeight}px`;
    }
  }, [minHeight]);

  useEffect(() => {
    setTextValue(value);
  }, [value]);

  useEffect(() => {
    // Adjust height when textValue changes
    adjustHeight();
  }, [textValue, adjustHeight]);

  useEffect(() => {
    return () => clearTimeout(submitTimer.current);
  }, []);
  
  return (
    <div style={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
      {caption && <div style={{ ...captionStyle, color: white ? "#ffffff88" : "#999" }}>{caption}</div>}
      <textarea
        ref={(el) => {
          // Handle both refs
          if (typeof ref === 'function') {
            ref(el);
          } else if (ref) {
            ref.current = el;
          }
          textareaRef.current = el;
        }}
        className={white ? "textarea-white" : "textarea"}
        style={{
          ...textareaStyle,
          textAlign: left ? "left" : "center",
          height: `${textareaHeight}px`,
          overflow: 'hidden', // Hide scrollbar during resize
          resize: 'none', // Disable manual resize since we're auto-sizing
          ...(style || {}),
          ...(customStyle || {}),
        }}
        onChange={(event) => {
          setTextValue(event.target.value);
          hasChanged.current = true;
          submit(event);
          // Adjust height after text change
          setTimeout(adjustHeight, 0);
        }}
        onBlur={(event) => {
          if (hasChanged.current) submit(event, true);
        }}
        value={textValue || ""}
        {...rest}
      />
    </div>
  );
});

export default Textarea;
